/**
 * P12 — Recent SLA / dwell-time alert feed.
 *
 * Renders only when the tenant has any dwell alerts configured AND the
 * sweeper has actually fired at least one. Empty state silently
 * collapses so non-engine tenants (or tenants without alerts) see no
 * dead UI on their dashboard.
 *
 * Read-only on purpose: alerting decisions belong to the workflow JSON;
 * the feed is a "what happened" view, not a "let me approve" workflow.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BellRing, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface DwellAlertRow {
  id: number;
  assetId: string;
  workflowStateId: number;
  alertCode: string;
  stateEnteredAt: string;
  firedAt: string;
  severity: 'info' | 'warning' | 'critical' | string;
}

interface DwellAlertsResponse {
  items: DwellAlertRow[];
}

async function fetchDwellAlerts(): Promise<DwellAlertRow[]> {
  try {
    const res = await api.get<DwellAlertsResponse>('/api/v2/workflows/dwell-alerts?limit=20');
    return res.data.items ?? [];
  } catch (err: unknown) {
    // 404 = workflows feature flag off OR SGS tenant. Silently empty.
    if ((err as { response?: { status?: number } }).response?.status === 404) return [];
    throw err;
  }
}

export function DwellAlertsCard() {
  const { t } = useTranslation('workflows');
  const query = useQuery({
    queryKey: ['workflow-dwell-alerts'],
    queryFn: fetchDwellAlerts,
    // Sweep cadence is 15 min; refetch on a similar cadence so the card
    // stays close to live without thrashing.
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  // Hide entirely when there's nothing to show. The card is opt-in by
  // virtue of dwellAlerts[] being configured — empty data means it
  // genuinely doesn't apply on this tenant.
  if (query.isPending) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <BellRing className="h-4 w-4" /> {t('dwell.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (!query.data || query.data.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <BellRing className="h-4 w-4" /> {t('dwell.title')}
        </CardTitle>
        <CardDescription>{t('dwell.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {query.data.map((row) => (
            <li
              key={row.id}
              className="flex items-start justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2"
            >
              <div className="flex items-start gap-2">
                <SeverityIcon severity={row.severity} />
                <div className="space-y-0.5 text-sm">
                  <div className="font-medium">
                    {row.assetId}{' '}
                    <span className="font-normal text-muted-foreground">— {row.alertCode}</span>
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {t('dwell.firedAt', { date: new Date(row.firedAt).toLocaleString() })}
                  </div>
                </div>
              </div>
              <SeverityBadge severity={row.severity} />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function SeverityIcon({ severity }: { severity: string }) {
  switch (severity) {
    case 'critical':
      return <ShieldAlert className="mt-0.5 h-4 w-4 text-destructive" />;
    case 'warning':
      return <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />;
    default:
      return <BellRing className="mt-0.5 h-4 w-4 text-muted-foreground" />;
  }
}

function SeverityBadge({ severity }: { severity: string }) {
  const variant: 'destructive' | 'secondary' | 'outline' =
    severity === 'critical' ? 'destructive' : severity === 'warning' ? 'secondary' : 'outline';
  return <Badge variant={variant}>{severity}</Badge>;
}
